import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import DataTable, { TableColumn } from "react-data-table-component";
import { HeartPulse, AlertTriangle, Users, TrendingUp, TrendingDown, Activity, ExternalLink, Download, ArrowUp, ArrowDown, Minus } from "lucide-react";
import { getApiBaseUrl } from "../utils/baseUrl";
import { useLoading, acquireTokenSafe } from "../App";

interface ClientHealthScore {
    id: number;
    business_id: number;
    name: string;
    client_state: string | null;
    overall_score: number;
    comm_freq_score: number | null;
    inv_stat_score: number | null;
    sentiment_score: number | null;
    gpm_score: number | null;
    score_date: string;
    previous_score: number | null;
    score_change: number | null;
}

interface HealthStats {
    total: number;
    healthy: number;
    moderate: number;
    atRisk: number;
    critical: number;
    avgScore: number;
}

type FilterCategory = 'all' | 'healthy' | 'moderate' | 'at-risk' | 'critical';
type TimePeriod = 'week' | 'month' | '3month';

const ClientHealth: React.FC = () => {
    const { instance, accounts } = useMsal();
    const { setLoading } = useLoading();
    const [clients, setClients] = useState<ClientHealthScore[]>([]);
    const [filteredClients, setFilteredClients] = useState<ClientHealthScore[]>([]);
    const [search, setSearch] = useState("");
    const [categoryFilter, setCategoryFilter] = useState<FilterCategory>("all");
    const [timePeriod, setTimePeriod] = useState<TimePeriod>("week");
    const [error, setError] = useState<string | null>(null);
    const [stats, setStats] = useState<HealthStats>({
        total: 0, healthy: 0, moderate: 0, atRisk: 0, critical: 0, avgScore: 0
    });

    useEffect(() => {
        const fetchClientHealth = async () => {
            if (accounts.length === 0) {
                console.log('ClientHealth: No accounts available yet');
                return;
            }

            setLoading(true);
            try {
                console.log('ClientHealth: Acquiring token...');
                const token = await acquireTokenSafe(instance, accounts);
                if (!token) { setLoading(false); return; }
                console.log('ClientHealth: Token acquired, fetching client health...');

                const response = await fetch(`${getApiBaseUrl()}/api/client-health?period=${timePeriod}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                console.log('ClientHealth: Response status:', response.status);
                
                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('ClientHealth: Response error:', errorText);
                    throw new Error(`Failed to fetch client health: ${response.status}`);
                }

                const data = await response.json();
                console.log('ClientHealth: Received', data.clients?.length, 'clients');
                setClients(data.clients || []);
                setFilteredClients(data.clients || []);
                setStats(data.stats || stats);
            } catch (err: any) {
                console.error('ClientHealth: Error:', err);
                setError(err.message || 'Failed to load client health');
            } finally {
                setLoading(false);
            }
        };

        fetchClientHealth();
    }, [instance, accounts, setLoading, timePeriod]);

    // Get health category from score
    const getHealthCategory = (score: number): string => {
        if (score >= 80) return 'healthy';
        if (score >= 60) return 'moderate';
        if (score >= 40) return 'at-risk';
        return 'critical';
    };

    const getCategoryLabel = (score: number): string => {
        if (score >= 80) return 'Healthy';
        if (score >= 60) return 'Moderate';
        if (score >= 40) return 'At Risk';
        return 'Critical';
    };

    useEffect(() => {
        let filtered = clients;

        // Apply category filter
        if (categoryFilter !== "all") {
            filtered = filtered.filter(c => getHealthCategory(c.overall_score) === categoryFilter);
        }

        // Apply search filter
        if (search) {
            const searchLower = search.toLowerCase();
            filtered = filtered.filter(
                (client) =>
                    client.name?.toLowerCase().includes(searchLower) ||
                    client.client_state?.toLowerCase().includes(searchLower)
            );
        }

        setFilteredClients(filtered);
    }, [search, categoryFilter, clients]);

    // Top gainers and losers
    const topGainers = React.useMemo(() => {
        return [...clients]
            .filter(c => c.score_change !== null && c.score_change > 0)
            .sort((a, b) => (b.score_change || 0) - (a.score_change || 0))
            .slice(0, 5);
    }, [clients]);

    const topLosers = React.useMemo(() => {
        return [...clients]
            .filter(c => c.score_change !== null && c.score_change < 0)
            .sort((a, b) => (a.score_change || 0) - (b.score_change || 0))
            .slice(0, 5);
    }, [clients]);

    const columns: TableColumn<ClientHealthScore>[] = [
        {
            name: "Client",
            selector: (row) => row.name,
            sortable: true,
            cell: (row) => (
                <div className="client-cell">
                    <div className="client-name">{row.name}</div>
                    {row.client_state && (
                        <div className="client-state">{row.client_state}</div>
                    )}
                </div>
            ),
            grow: 2,
            minWidth: "250px"
        },
        {
            name: "Health Score",
            selector: (row) => row.overall_score,
            sortable: true,
            cell: (row) => (
                <div className="score-cell">
                    <div className={`score-badge ${getHealthCategory(row.overall_score)}`}>
                        {row.overall_score}
                    </div>
                    {row.score_change !== null && row.score_change !== 0 && (
                        <span className={`score-change ${row.score_change > 0 ? 'positive' : 'negative'}`}>
                            {row.score_change > 0 ? <ArrowUp size={12} /> : <ArrowDown size={12} />}
                            {Math.abs(row.score_change)}
                        </span>
                    )}
                </div>
            ),
            width: "150px"
        },
        {
            name: "Status",
            selector: (row) => getHealthCategory(row.overall_score),
            sortable: true,
            cell: (row) => (
                <span className={`status-badge ${getHealthCategory(row.overall_score)}`}>
                    {getCategoryLabel(row.overall_score)}
                </span>
            ),
            width: "120px"
        },
        {
            name: "Communication",
            selector: (row) => row.comm_freq_score || 0,
            sortable: true,
            cell: (row) => (
                <span className="sub-score">{row.comm_freq_score ?? '—'}/20</span>
            ),
            center: true,
            width: "120px"
        },
        {
            name: "Invoicing",
            selector: (row) => row.inv_stat_score || 0,
            sortable: true,
            cell: (row) => (
                <span className="sub-score">{row.inv_stat_score ?? '—'}/40</span>
            ),
            center: true,
            width: "100px"
        },
        {
            name: "Sentiment",
            selector: (row) => row.sentiment_score || 0,
            sortable: true,
            cell: (row) => (
                <span className="sub-score">{row.sentiment_score ?? '—'}/20</span>
            ),
            center: true,
            width: "100px"
        },
        {
            name: "GPM",
            selector: (row) => row.gpm_score || 0,
            sortable: true,
            cell: (row) => (
                <span className="sub-score">{row.gpm_score ?? '—'}/20</span>
            ),
            center: true,
            width: "80px"
        },
        {
            name: "Action",
            cell: (row) => (
                <a
                    href={`https://my.kinore.com/admin/clienthealth?b_id=${row.business_id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="action-btn"
                    title="View in Kinore Platform"
                >
                    <ExternalLink size={14} />
                </a>
            ),
            width: "60px",
            center: true
        }
    ];

    // CSV Download function
    const downloadCSV = () => {
        const headers = [
            'Business ID',
            'Client Name',
            'Client State',
            'Overall Score',
            'Status',
            'Communication Score',
            'Invoicing Score',
            'Sentiment Score',
            'GPM Score',
            'Score Change',
            'Score Date'
        ];

        const csvData = filteredClients.map(client => [
            client.business_id,
            client.name,
            client.client_state || '',
            client.overall_score,
            getCategoryLabel(client.overall_score),
            client.comm_freq_score ?? '',
            client.inv_stat_score ?? '',
            client.sentiment_score ?? '',
            client.gpm_score ?? '',
            client.score_change ?? '',
            client.score_date
        ]);

        const escapeCSV = (value: any): string => {
            const str = String(value ?? '');
            if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                return `"${str.replace(/"/g, '""')}"`;
            }
            return str;
        };

        const csvContent = [
            headers.map(escapeCSV).join(','),
            ...csvData.map(row => row.map(escapeCSV).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', `client-health-${new Date().toISOString().split('T')[0]}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (error) {
        return (
            <div className="page-container">
                <div className="page-header">
                    <div className="page-title">
                        <HeartPulse size={28} />
                        <h1>Client Health</h1>
                    </div>
                </div>
                <div className="warning-banner">
                    <AlertTriangle size={20} />
                    <span>Error loading client health: {error}</span>
                </div>
            </div>
        );
    }

    return (
        <div className="page-container">
            <div className="page-header compact">
                <div className="page-title">
                    <HeartPulse size={20} />
                    <h1>Client Health</h1>
                </div>
            </div>

            <div className="stats-grid stats-grid-6">
                <div className="stat-card">
                    <div className="stat-icon">
                        <Users size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.total}</div>
                        <div className="stat-label">Total Clients</div>
                    </div>
                </div>
                <div className="stat-card healthy">
                    <div className="stat-icon">
                        <TrendingUp size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.healthy}</div>
                        <div className="stat-label">Healthy (80+)</div>
                    </div>
                </div>
                <div className="stat-card moderate">
                    <div className="stat-icon">
                        <Activity size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.moderate}</div>
                        <div className="stat-label">Moderate (60-79)</div>
                    </div>
                </div>
                <div className="stat-card at-risk">
                    <div className="stat-icon">
                        <AlertTriangle size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.atRisk}</div>
                        <div className="stat-label">At Risk (40-59)</div>
                    </div>
                </div>
                <div className="stat-card critical">
                    <div className="stat-icon">
                        <TrendingDown size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.critical}</div>
                        <div className="stat-label">Critical (&lt;40)</div>
                    </div>
                </div>
                <div className="stat-card highlight">
                    <div className="stat-icon">
                        <HeartPulse size={20} />
                    </div>
                    <div className="stat-content">
                        <div className="stat-value">{stats.avgScore.toFixed(0)}</div>
                        <div className="stat-label">Avg Score</div>
                    </div>
                </div>
            </div>

            {/* Top Movers Section - Coinbase Style */}
            <div className="movers-section">
                <div className="movers-period-toggle">
                    <span className="period-label">Compare over:</span>
                    <div className="period-buttons">
                        <button 
                            className={`period-btn ${timePeriod === 'week' ? 'active' : ''}`}
                            onClick={() => setTimePeriod('week')}
                        >
                            7 Days
                        </button>
                        <button 
                            className={`period-btn ${timePeriod === 'month' ? 'active' : ''}`}
                            onClick={() => setTimePeriod('month')}
                        >
                            30 Days
                        </button>
                        <button 
                            className={`period-btn ${timePeriod === '3month' ? 'active' : ''}`}
                            onClick={() => setTimePeriod('3month')}
                        >
                            3 Months
                        </button>
                    </div>
                </div>
                <div className="movers-grid">
                    <div className="movers-card gainers">
                        <div className="movers-header">
                            <TrendingUp size={18} />
                            <h3>Top Gainers</h3>
                        </div>
                        <div className="movers-list">
                            {topGainers.length === 0 ? (
                                <div className="no-movers">No significant gains</div>
                            ) : (
                                topGainers.map((client, idx) => (
                                    <a
                                        key={client.id}
                                        href={`https://my.kinore.com/admin/clienthealth?b_id=${client.business_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mover-item"
                                    >
                                        <span className="mover-rank">{idx + 1}</span>
                                        <span className="mover-name">{client.name}</span>
                                        <span className="mover-score">{client.overall_score}</span>
                                        <span className="mover-change positive">
                                            <ArrowUp size={12} />
                                            +{client.score_change}
                                        </span>
                                    </a>
                                ))
                            )}
                        </div>
                    </div>
                    <div className="movers-card losers">
                        <div className="movers-header">
                            <TrendingDown size={18} />
                            <h3>Top Losers</h3>
                        </div>
                        <div className="movers-list">
                            {topLosers.length === 0 ? (
                                <div className="no-movers">No significant declines</div>
                            ) : (
                                topLosers.map((client, idx) => (
                                    <a
                                        key={client.id}
                                        href={`https://my.kinore.com/admin/clienthealth?b_id=${client.business_id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="mover-item"
                                    >
                                        <span className="mover-rank">{idx + 1}</span>
                                        <span className="mover-name">{client.name}</span>
                                        <span className="mover-score">{client.overall_score}</span>
                                        <span className="mover-change negative">
                                            <ArrowDown size={12} />
                                            {client.score_change}
                                        </span>
                                    </a>
                                ))
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="table-controls">
                <input
                    type="text"
                    placeholder="Search clients..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="search-input"
                />
                <select
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value as FilterCategory)}
                    className="filter-select"
                >
                    <option value="all">All Clients ({stats.total})</option>
                    <option value="healthy">Healthy ({stats.healthy})</option>
                    <option value="moderate">Moderate ({stats.moderate})</option>
                    <option value="at-risk">At Risk ({stats.atRisk})</option>
                    <option value="critical">Critical ({stats.critical})</option>
                </select>
                <button
                    onClick={downloadCSV}
                    className="download-btn"
                    title="Download as CSV"
                >
                    <Download size={16} />
                    Export CSV
                </button>
            </div>

            <div className="data-table-container client-health-table">
                <DataTable
                    columns={columns}
                    data={filteredClients}
                    pagination
                    paginationPerPage={15}
                    paginationRowsPerPageOptions={[15, 25, 50, 100]}
                    highlightOnHover
                    responsive
                    defaultSortFieldId={2}
                    defaultSortAsc={false}
                    noDataComponent={
                        <div className="no-data">No clients found</div>
                    }
                    customStyles={{
                        table: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                borderRadius: '8px',
                            },
                        },
                        noData: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-muted)',
                            },
                        },
                        headRow: {
                            style: {
                                backgroundColor: 'var(--bg-tertiary)',
                                borderBottomColor: 'var(--border-color)',
                                color: 'var(--text-primary)',
                                fontWeight: 600,
                                fontSize: '0.85rem',
                            },
                        },
                        headCells: {
                            style: {
                                color: 'var(--text-secondary)',
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            },
                        },
                        rows: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-primary)',
                                borderBottomColor: 'var(--border-color)',
                            },
                            highlightOnHoverStyle: {
                                backgroundColor: 'var(--bg-hover)',
                                color: 'var(--text-primary)',
                                borderBottomColor: 'var(--border-color)',
                            },
                        },
                        cells: {
                            style: {
                                paddingLeft: '16px',
                                paddingRight: '16px',
                            },
                        },
                        pagination: {
                            style: {
                                backgroundColor: 'var(--bg-secondary)',
                                color: 'var(--text-secondary)',
                                borderTopColor: 'var(--border-color)',
                            },
                            pageButtonsStyle: {
                                color: 'var(--text-secondary)',
                                fill: 'var(--text-secondary)',
                                '&:hover:not(:disabled)': {
                                    backgroundColor: 'var(--bg-hover)',
                                },
                                '&:disabled': {
                                    opacity: 0.4,
                                },
                            },
                        },
                    }}
                />
            </div>

            <style>{`
                /* Health Status Card Colors */
                .stat-card.healthy {
                    border-left: 3px solid #10b981;
                }
                .stat-card.healthy .stat-icon {
                    color: #10b981;
                }
                .stat-card.moderate {
                    border-left: 3px solid #f59e0b;
                }
                .stat-card.moderate .stat-icon {
                    color: #f59e0b;
                }
                .stat-card.at-risk {
                    border-left: 3px solid #f97316;
                }
                .stat-card.at-risk .stat-icon {
                    color: #f97316;
                }
                .stat-card.critical {
                    border-left: 3px solid #ef4444;
                }
                .stat-card.critical .stat-icon {
                    color: #ef4444;
                }

                /* Movers Section */
                .movers-section {
                    margin-bottom: 20px;
                }
                .movers-period-toggle {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 12px;
                }
                .period-label {
                    font-size: 0.85rem;
                    color: var(--text-secondary);
                    font-weight: 500;
                }
                .period-buttons {
                    display: flex;
                    gap: 4px;
                    background: var(--bg-tertiary);
                    padding: 4px;
                    border-radius: 8px;
                }
                .period-btn {
                    padding: 6px 12px;
                    font-size: 0.8rem;
                    font-weight: 500;
                    border: none;
                    background: transparent;
                    color: var(--text-secondary);
                    border-radius: 6px;
                    cursor: pointer;
                    transition: all 0.15s;
                }
                .period-btn:hover {
                    color: var(--text-primary);
                    background: var(--bg-hover);
                }
                .period-btn.active {
                    background: var(--accent-color);
                    color: white;
                }
                .movers-grid {
                    display: grid;
                    grid-template-columns: repeat(2, 1fr);
                    gap: 16px;
                }
                @media (max-width: 768px) {
                    .movers-grid {
                        grid-template-columns: 1fr;
                    }
                }
                .movers-card {
                    background: var(--bg-secondary);
                    border-radius: 8px;
                    padding: 16px;
                    border: 1px solid var(--border-color);
                }
                .movers-header {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 12px;
                    padding-bottom: 12px;
                    border-bottom: 1px solid var(--border-color);
                }
                .movers-header h3 {
                    margin: 0;
                    font-size: 0.9rem;
                    font-weight: 600;
                    color: var(--text-primary);
                }
                .movers-card.gainers .movers-header {
                    color: #10b981;
                }
                .movers-card.losers .movers-header {
                    color: #ef4444;
                }
                .movers-list {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }
                .mover-item {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 8px;
                    border-radius: 6px;
                    background: var(--bg-tertiary);
                    text-decoration: none;
                    transition: background 0.15s;
                }
                .mover-item:hover {
                    background: var(--bg-hover);
                }
                .mover-rank {
                    width: 20px;
                    height: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 0.75rem;
                    font-weight: 600;
                    color: var(--text-muted);
                    background: var(--bg-secondary);
                    border-radius: 4px;
                }
                .mover-name {
                    flex: 1;
                    font-size: 0.85rem;
                    color: var(--text-primary);
                    white-space: nowrap;
                    overflow: hidden;
                    text-overflow: ellipsis;
                }
                .mover-score {
                    font-size: 0.85rem;
                    font-weight: 600;
                    color: var(--text-primary);
                }
                .mover-change {
                    display: flex;
                    align-items: center;
                    gap: 2px;
                    font-size: 0.8rem;
                    font-weight: 500;
                    min-width: 50px;
                    justify-content: flex-end;
                }
                .mover-change.positive {
                    color: #10b981;
                }
                .mover-change.negative {
                    color: #ef4444;
                }
                .no-movers {
                    font-size: 0.85rem;
                    color: var(--text-muted);
                    text-align: center;
                    padding: 16px;
                }

                /* Client Cell */
                .client-health-table .client-cell {
                    padding: 8px 0;
                }
                .client-health-table .client-name {
                    font-weight: 500;
                    color: var(--text-primary);
                }
                .client-health-table .client-state {
                    font-size: 0.75rem;
                    color: var(--text-muted);
                    margin-top: 2px;
                }

                /* Score Cell */
                .score-cell {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                }
                .score-badge {
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    min-width: 40px;
                    padding: 4px 10px;
                    border-radius: 6px;
                    font-weight: 600;
                    font-size: 0.9rem;
                }
                .score-badge.healthy {
                    background: rgba(16, 185, 129, 0.15);
                    color: #10b981;
                }
                .score-badge.moderate {
                    background: rgba(245, 158, 11, 0.15);
                    color: #f59e0b;
                }
                .score-badge.at-risk {
                    background: rgba(249, 115, 22, 0.15);
                    color: #f97316;
                }
                .score-badge.critical {
                    background: rgba(239, 68, 68, 0.15);
                    color: #ef4444;
                }
                .score-change {
                    display: flex;
                    align-items: center;
                    gap: 2px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }
                .score-change.positive {
                    color: #10b981;
                }
                .score-change.negative {
                    color: #ef4444;
                }

                /* Status Badge */
                .client-health-table .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 4px 10px;
                    border-radius: 12px;
                    font-size: 0.75rem;
                    font-weight: 500;
                }
                .client-health-table .status-badge.healthy {
                    background: rgba(16, 185, 129, 0.15);
                    color: #10b981;
                }
                .client-health-table .status-badge.moderate {
                    background: rgba(245, 158, 11, 0.15);
                    color: #f59e0b;
                }
                .client-health-table .status-badge.at-risk {
                    background: rgba(249, 115, 22, 0.15);
                    color: #f97316;
                }
                .client-health-table .status-badge.critical {
                    background: rgba(239, 68, 68, 0.15);
                    color: #ef4444;
                }

                /* Sub Score */
                .sub-score {
                    font-size: 0.85rem;
                    color: var(--text-secondary);
                }
            `}</style>
        </div>
    );
};

export default ClientHealth;
